import moment from "moment";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toolbar } from "primereact/toolbar";
import { Tooltip } from "primereact/tooltip";
import React, { useContext, useRef } from "react";
import { useHistory } from "react-router";
import Labels from "../../../infrastructure/Labels_sr_Latn_RS";
import { DATE_FORMAT, formatCustomDate } from "../../../utils/Utils";
import ExportData from "../../export/ExportData";
import { AppContext } from "../../Store";

const EXPORT_FILE_NAME = "rezultati_pretrage";

interface RegistarDataTableProps {
  dataTable: any[];
  loading: boolean;
  tableRows: number;
  first: number;
  handleRowsAndFirst: (rows: number, first: number) => void;
  tipoviSkupaAtributa: any[];
  openDialogUnos: (pacijentID: string) => void;
  openDialogPacijent: () => void;
  displayUnosStatus: boolean;
}

export default function RegistarDataTable(props: RegistarDataTableProps) {
  const { dataTable, loading, tableRows, first, handleRowsAndFirst, tipoviSkupaAtributa, openDialogUnos, openDialogPacijent, displayUnosStatus } = props;
  const history = useHistory();
  const dt = useRef<any>();
  const blurText = (rowData: any) => (rowData.blurPodataka ? "blur-text" : undefined);
  const { prikazDatumaPoseban, checkFilter } = useContext(AppContext);

  const pacijentTemplate = (rowData: any) =>
    !rowData.blurPodataka ? (
      <Button
        label={checkFilter("jsp", { defaultValue: false }) ? `${Labels.PLACEHOLDER_JSP} ${rowData.jsp}` : rowData.imePrezime}
        onClick={() => history.push(`/pacijenti/${rowData.pacijentID}`)}
        className="p-button-outlined"
        style={{ maxWidth: "100%", width: "14rem" }}
      />
    ) : undefined;

  const jmbgTemplate = (rowData: any) => <div className={blurText(rowData)}>{rowData.jmbg}</div>;

  const lboTemplate = (rowData: any) => <div className={blurText(rowData)}>{rowData.lbo}</div>;

  const identifikacioniBrojTemplate = (rowData: any) => <div className={blurText(rowData)}>{rowData.identifikacioniBroj}</div>;

  const prviUnosTemplate = (rowData: any) =>
    !rowData.blurPodataka && rowData.prviUnos ? (
      <Button
        icon="pi pi-file"
        label={
          rowData.prviUnosNaziv
            ? rowData.prviUnosNaziv +
              " (" +
              rowData.prviUnosDatum +
              ")" +
              (prikazDatumaPoseban && rowData.prviUnosDatum ? ` - ${formatCustomDate(moment(rowData.prviUnosDatum, DATE_FORMAT).toDate())}` : "")
            : ""
        }
        onClick={() => history.push(`/unosi/${rowData.prviUnosID}`)}
        className="p-button-outlined w-full"
      ></Button>
    ) : undefined;

  const poslednjiUnosTemplate = (rowData: any) =>
    !rowData.blurPodataka && rowData.poslednjiUnos ? (
      <Button
        icon="pi pi-file"
        label={
          rowData.poslednjiUnosNaziv
            ? rowData.poslednjiUnosNaziv +
              " (" +
              rowData.poslednjiUnosDatum +
              ")" +
              (prikazDatumaPoseban && rowData.poslednjiUnosDatum ? ` - ${formatCustomDate(moment(rowData.poslednjiUnosDatum, DATE_FORMAT).toDate())}` : "")
            : ""
        }
        onClick={() => history.push(`/unosi/${rowData.poslednjiUnosID}`)}
        className="p-button-outlined w-full"
      />
    ) : undefined;

  const noviUnosTemplate = (rowData: any) =>
    !rowData.blurPodataka && tipoviSkupaAtributa?.length ? (
      <Button className="p-button-outlined w-full" icon="fas fa-plus" label={Labels.BUTTON_NOVI_UNOS} onClick={() => openDialogUnos(rowData.pacijentID)} />
    ) : undefined;

  const leftToolbarTemplate = () => <Button label={Labels.BUTTON_NOVI_PACIJENT} icon="pi pi-plus" className="p-button-info p-button-outlined p-mr-2" onClick={openDialogPacijent} />;

  const rightToolbarTemplate = () => checkFilter("export") && <ExportData data={dataTable} name={EXPORT_FILE_NAME} dt={dt} />;

  return (
    <>
      <div className="col-12">
        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} />
      </div>
      <div className="col-12">
        <DataTable
          ref={dt}
          value={dataTable}
          loading={loading}
          paginator
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          first={first}
          rows={tableRows}
          rowsPerPageOptions={[5, 10, 20, 25, 50, 100]}
          onPage={(e) => handleRowsAndFirst(e.rows, e.first)}
          emptyMessage={Labels.TABLE_EMPTY_MESSAGE}
          currentPageReportTemplate={Labels.PAGINATION_SHOWING_TO_OF_ENTRIES}
          exportFilename={EXPORT_FILE_NAME}
        >
          {displayUnosStatus && (
            <Column
              field="mesecniUnosiPopunjeni"
              header={
                <div className="flex justify-content-center">
                  <i className="pi pi-file popunjeni-svi-tooltip" data-pr-tooltip="Popunjeni svi unosi" data-pr-position="top" />
                  <Tooltip target=".popunjeni-svi-tooltip" />
                </div>
              }
              className="w-3rem"
              sortable
              body={(e) =>
                e.mesecniUnosiPopunjeni ? (
                  <div className="flex justify-content-center">
                    <i className="pi pi-check-circle text-xl" style={{ color: "green" }} />
                  </div>
                ) : (
                  ""
                )
              }
            />
          )}
          <Column
            field="imePrezime"
            header={Labels.COLUMN_HEADER_PACIJENT}
            sortable
            body={pacijentTemplate}
            style={{
              width:
                checkFilter("jsp", { defaultValue: false }) &&
                (checkFilter("jmbg") || checkFilter("lbo") || checkFilter("identifikacioniBroj") || checkFilter("prviUnos") || checkFilter("poslednjiUnos"))
                  ? "15rem"
                  : undefined,
            }}
          />
          {checkFilter("jmbg") && <Column field="jmbg" header={Labels.COLUMN_HEADER_JMBG} sortable body={jmbgTemplate} />}
          {checkFilter("lbo") && <Column field="lbo" header={Labels.COLUMN_HEADER_LBO} sortable body={lboTemplate} />}
          {checkFilter("identifikacioniBroj") && <Column field="identifikacioniBroj" header={Labels.COLUMN_HEADER_IDENTIFIKACIONI_BROJ} sortable body={identifikacioniBrojTemplate} />}
          {checkFilter("prviUnos") && <Column field="prviUnos" header={Labels.COLUMN_HEADER_PRVI_UNOS} sortable body={prviUnosTemplate} />}
          {checkFilter("poslednjiUnos") && <Column field="poslednjiUnos" header={Labels.COLUMN_HEADER_POSLEDNJI_UNOS} sortable body={poslednjiUnosTemplate} />}
          <Column field="noviUnos" style={{ width: "10rem" }} header={Labels.COLUMN_HEADER_NOVI_UNOS} sortable body={noviUnosTemplate} exportable={false} />
          <Column field="blurPodataka" hidden exportable={false} />
        </DataTable>
      </div>
      {checkFilter("nazadNaRegistre") && (
        <div className="col">
          <Toolbar left={<Button icon="pi pi-undo" className="p-button-outlined p-button-danger" label={Labels.BUTTON_NAZAD_NA_REGISTRE} onClick={() => history.push("/registri")} />} />
        </div>
      )}
    </>
  );
}
